import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentUser } from "@/hooks/useCurrentUser";

/**
 * Status, die als „offen“ zählen, so wie sie in `support_tickets.status` stehen: mit Bindestrich.
 * Bis 10.10.2026 stand hier `in_bearbeitung` und `warten_auf_nutzer`; damit übersah der Hinweis
 * genau die Tickets, die auf eine Antwort warten (Sitzung A, `docs/strategie/neuaufstellung-2027.md` §9k.6).
 */
export const OFFENE_TICKET_STATUS = ["offen", "in-bearbeitung", "warten-auf-nutzer"] as const;

/**
 * Zahl der offenen Tickets für die Seitenleiste. Läuft in `StudentLayout` auf jeder Seite, auch
 * unter der neuen Ansicht. Das Konto kommt aus der Sitzung (`useCurrentUser`, gleicher Schlüssel wie
 * überall), nicht aus `supabase.auth.getUser()`: Das ist ein Netzwerkaufruf je Seite (CLAUDE.md).
 */
export function useOpenTickets() {
  const { data: user } = useCurrentUser();
  const userId = user?.id ?? null;

  const { data: count = 0 } = useQuery({
    queryKey: ["open-tickets-count", userId],
    queryFn: async () => {
      if (!userId) return 0;
      const { count, error } = await supabase
        .from("support_tickets")
        .select("id", { count: "exact", head: true })
        .eq("user_id", userId)
        .in("status", [...OFFENE_TICKET_STATUS]);
      if (error) return 0;
      return count ?? 0;
    },
    enabled: !!userId,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  return count;
}
